import { useQuery } from '@apollo/client';
import { READ_RECEIPT_BY_TYPE } from 'graphql/queries/readReceiptByType';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Query,
  QueryReadReceiptsByTypeArgs,
  ReceiptInput,
  ReceiptItemType,
} from 'types/generatedSchemaTypes';
import { sortFilter, updateReceiptQuery } from 'utils/parkUtils';
import useParkingHistoryFilter from './useParkingHistoryFilter';

const PAGE_SIZE = 50;

interface UseReadReceiptsResult {
  data: Query['readReceiptsByType'] | undefined;
  loading: boolean;
  error: any;
  fetchMoreCallback: () => void;
  resetPage: () => void;
  refetch: () => void;
}

const createReceiptInput = (
  page: number,
  receiptType: ReceiptItemType[],
  licensePlate?: string
): ReceiptInput => ({
  itemType: receiptType,
  licensePlate: licensePlate,
  page,
  size: PAGE_SIZE,
  sort: sortFilter,
});

const useReadReceipts = (
  receiptType: ReceiptItemType[]
): UseReadReceiptsResult => {
  const [currentPage, setCurrentPage] = useState(0);
  const { vehicle: vehicleFilter } = useParkingHistoryFilter();
  const subscriptionFetch = receiptType.includes('AIMO_PLUS');
  const licensePlateText = subscriptionFetch
    ? undefined
    : vehicleFilter?.licensePlate?.plateText;

  const resetPage = useCallback(() => setCurrentPage(0), []);
  const nextPage = useCallback(() => setCurrentPage((prev) => prev + 1), []);

  const initialInput = useMemo<ReceiptInput>(
    () => createReceiptInput(0, receiptType, licensePlateText),
    [receiptType, licensePlateText]
  );
  const pagingInput = useMemo<ReceiptInput>(
    () => createReceiptInput(currentPage, receiptType, licensePlateText),
    [currentPage, receiptType, licensePlateText]
  );

  const { data, loading, error, fetchMore, refetch } = useQuery<
    Query,
    QueryReadReceiptsByTypeArgs
  >(READ_RECEIPT_BY_TYPE, {
    variables: {
      input: initialInput,
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  // Reset page and refetch when license plate changes
  useEffect(() => {
    resetPage();
    refetch({
      input: initialInput,
    });
  }, [refetch, resetPage, initialInput]);

  // Fetch more data when page changes
  useEffect(() => {
    if (currentPage !== 0) {
      fetchMore({
        variables: {
          input: pagingInput,
        },
        updateQuery: updateReceiptQuery,
      });
    }
  }, [currentPage, fetchMore, pagingInput]);

  const fetchMoreCallback = () => {
    if (!data?.readReceiptsByType?.last) {
      nextPage();
    }
  };

  const refetchReceipts = useCallback(
    () => refetch({ input: initialInput }),
    [refetch, initialInput]
  );

  return {
    data: data?.readReceiptsByType,
    loading,
    error,
    fetchMoreCallback,
    resetPage,
    refetch: refetchReceipts,
  };
};

export default useReadReceipts;
